import type { FC, ReactElement } from "react"

import { useHtmlHead, HtmlHeadTags } from "~/shared/ui/hooks"

/**
 * Использует хук {@link useHtmlHead}. Оборачиваемый компонент выведется после загрузки указанных скриптов
 * @param htmlHeadTags
 * @param fallbackFn функция, которая возвращает элемент, выводимый до загрузки скриптов (например, индикатор загрузки)
 * @hoc
 * @category HOC
 * @example
 * ```tsx
 * import {useEffect} from "react";
 * import {withHtmlHead} from "~/shared/hocs";
 *
 * function Example() {
 *
 *     useEffect(() => {
 *         // jQuery здесь только для примера
 *         const mydiv = jQuery("#mydiv"); // eslint-disable-line
 *         mydiv.text("Обёрнутый тестовый компонент");
 *     }, [])
 *
 *     return <div id="mydiv">Тестовый компонент</div>
 * }
 *
 * const WrappedExample = withHtmlHead({
 *     title: "Заголовок",
 *     scripts: [
 *         {
 *             src: "https://code.jquery.com/jquery-3.6.3.min.js",
 *             integrity: "sha256-pvPw+upLPUjgMXY0G+8O0xUf+/Im1MZjXxxgOcBQBXU=",
 *             crossorigin: "anonymous"
 *         }
 *     ]
 * })(Example);
 * ```
 */
export function withHtmlHead(
	{ title, links = [], meta = [], scripts = [] }: HtmlHeadTags,
	fallbackFn?: () => ReactElement,
) {
	return (C: FC) => {
		const WithHtmlHead = () => {
			const { scriptsLoaded, linksLoaded } = useHtmlHead({ title, links, meta, scripts })
			return scriptsLoaded && linksLoaded ? <C /> : fallbackFn?.()
		}

		WithHtmlHead.displayName = 'WithHtmlHead_' + (C.displayName ?? C.name)
		return WithHtmlHead
	}
}
